/**
 * ReelEmotional · iter v29 r048
 *
 * Reel verticale 1080×1920 · 18s · prima/dopo cinematico di una camera
 * reale. Stesso DNA grafico dei reel servizio (cover scuro Pronto +
 * outro "Al resto ci pensa Pronto"), ma fra le 2 cover c'è il delta
 * cinematic prima→dopo che è IL motivo per cui vendiamo.
 *
 * Timeline (totale 18s, ma il rendering scaler tag i tempi a 100%):
 *  - 0   →  16%   Cover scuro "Una foto basta."
 *  - 16  →  44%   PRIMA fullscreen + Ken Burns zoom-out + badge PRIMA
 *  - 44  →  53%   Transizione wipe-centrale magica
 *  - 53  →  78%   DOPO fullscreen + Ken Burns zoom-in + badge DOPO + dati
 *  - 78  →  88%   Stat-block (5 SECONDI · ZERO STAGING · 1 FOTO)
 *  - 88  → 100%   Outro Pronto
 *
 * Attivato col flag data-rec="1" come gli altri reel (Playwright setattr).
 */
import React from "react";

export default function ReelEmotional() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&family=Cormorant+Garamond:wght@500;600;700&display=swap');
        html, body { margin: 0; padding: 0; }
        [data-testid="cookie-banner"], .cookie-banner, .CookieConsent,
        [class*="cookie"], [id*="cookie"], [class*="Cookie"] { display: none !important; }

        [data-testid="reel-emotional"] { --rr-play: paused; }
        [data-testid="reel-emotional"][data-rec="1"] { --rr-play: running; }

        [data-testid="reel-emotional"] .rre-cover,
        [data-testid="reel-emotional"] .rre-cover > *,
        [data-testid="reel-emotional"] .rre-before,
        [data-testid="reel-emotional"] .rre-before-img,
        [data-testid="reel-emotional"] .rre-before-badge,
        [data-testid="reel-emotional"] .rre-before-overlay,
        [data-testid="reel-emotional"] .rre-after,
        [data-testid="reel-emotional"] .rre-after-img,
        [data-testid="reel-emotional"] .rre-after-badge,
        [data-testid="reel-emotional"] .rre-after-line1,
        [data-testid="reel-emotional"] .rre-after-line2,
        [data-testid="reel-emotional"] .rre-wipe,
        [data-testid="reel-emotional"] .rre-stat,
        [data-testid="reel-emotional"] .rre-stat > *,
        [data-testid="reel-emotional"] .rre-outro,
        [data-testid="reel-emotional"] .rre-outro > * {
          animation-play-state: var(--rr-play);
        }

        /* ─── COVER (0-16%) ────────────────────────────────────────────── */
        .rre-cover {
          position: absolute; inset: 0;
          background: #1C1C1A;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          z-index: 5; opacity: 1;
          animation: rreCoverOut 18s linear forwards;
        }
        @keyframes rreCoverOut {
          0%, 14% { opacity: 1; }
          17% { opacity: 0; }
          100% { opacity: 0; visibility: hidden; }
        }
        .rre-cover-mark {
          width: 200px; height: 200px; margin-bottom: 48px;
          filter: brightness(0) invert(0.93);
          opacity: 0; transform: scale(0.88);
          animation: rreMark 18s ease-out forwards;
        }
        @keyframes rreMark {
          0% { opacity: 0; transform: scale(0.88); }
          5% { opacity: 1; transform: scale(1); }
          17% { opacity: 1; transform: scale(1); }
          100% { opacity: 1; transform: scale(1); }
        }
        .rre-cover-line1 {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-style: italic; font-weight: 500;
          font-size: 90px; line-height: 1;
          color: #FAF8F4; letter-spacing: -0.012em;
          margin-bottom: 8px;
          opacity: 0; transform: translateY(20px);
          animation: rreLine1 18s ease-out forwards;
        }
        @keyframes rreLine1 {
          0%, 4% { opacity: 0; transform: translateY(20px); }
          9% { opacity: 1; transform: translateY(0); }
          17% { opacity: 1; transform: translateY(0); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .rre-cover-line2 {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700; font-size: 110px; line-height: 1;
          color: #A8B89A; letter-spacing: -0.02em;
          opacity: 0; transform: translateY(20px);
          animation: rreLine2 18s ease-out forwards;
        }
        @keyframes rreLine2 {
          0%, 7% { opacity: 0; transform: translateY(20px); }
          12% { opacity: 1; transform: translateY(0); }
          17% { opacity: 1; transform: translateY(0); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .rre-cover-tag {
          margin-top: 56px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 600; font-size: 22px;
          letter-spacing: 0.42em; color: rgba(255,255,255,0.45);
          text-transform: uppercase;
          opacity: 0;
          animation: rreCoverTag 18s ease-out forwards;
        }
        @keyframes rreCoverTag {
          0%, 10% { opacity: 0; }
          14% { opacity: 1; }
          17% { opacity: 1; }
          100% { opacity: 1; }
        }

        /* ─── PRIMA (16-44%) ────────────────────────────────────────────── */
        .rre-before {
          position: absolute; inset: 0;
          background: #000;
          z-index: 4; opacity: 0;
          animation: rreBeforeOpa 18s linear forwards;
        }
        @keyframes rreBeforeOpa {
          0%, 15% { opacity: 0; }
          18% { opacity: 1; }
          45% { opacity: 1; }
          48% { opacity: 0; }
          100% { opacity: 0; visibility: hidden; }
        }
        .rre-before-img {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          object-fit: cover; object-position: center 40%;
          transform: scale(1.18);
          animation: rreBeforeKenBurns 18s ease-out forwards;
        }
        @keyframes rreBeforeKenBurns {
          0%, 17% { transform: scale(1.18) translateX(0); }
          45% { transform: scale(1.08) translateX(-2%); }
          100% { transform: scale(1.08) translateX(-2%); }
        }
        .rre-before-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.78) 100%);
          opacity: 0;
          animation: rreBeforeOverlay 18s linear forwards;
        }
        @keyframes rreBeforeOverlay {
          0%, 16% { opacity: 0; }
          20% { opacity: 1; }
          45% { opacity: 1; }
          48% { opacity: 0; }
          100% { opacity: 0; }
        }
        .rre-before-badge {
          position: absolute; top: 120px; left: 60px;
          display: inline-flex; align-items: center; gap: 18px;
          padding: 14px 26px 14px 14px;
          background: rgba(255,255,255,0.96);
          border-radius: 999px;
          opacity: 0; transform: translateX(-30px);
          animation: rreBeforeBadge 18s ease-out forwards;
        }
        @keyframes rreBeforeBadge {
          0%, 17% { opacity: 0; transform: translateX(-30px); }
          22% { opacity: 1; transform: translateX(0); }
          45% { opacity: 1; transform: translateX(0); }
          48% { opacity: 0; transform: translateX(20px); }
          100% { opacity: 0; }
        }
        .rre-before-badge-dot {
          width: 36px; height: 36px;
          border-radius: 999px;
          background: #C45A3E;
          flex-shrink: 0;
        }
        .rre-before-badge-text {
          font-family: 'Montserrat', sans-serif;
          font-weight: 800; font-size: 28px;
          letter-spacing: 0.18em;
          color: #1C1C1A;
          text-transform: uppercase;
        }
        .rre-before-caption {
          position: absolute; bottom: 180px; left: 60px; right: 60px;
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-style: italic; font-weight: 500;
          font-size: 64px; line-height: 1.15;
          color: #FAF8F4; letter-spacing: -0.005em;
          opacity: 0; transform: translateY(20px);
          animation: rreBeforeCaption 18s ease-out forwards;
        }
        @keyframes rreBeforeCaption {
          0%, 25% { opacity: 0; transform: translateY(20px); }
          30% { opacity: 1; transform: translateY(0); }
          45% { opacity: 1; transform: translateY(0); }
          48% { opacity: 0; transform: translateY(-12px); }
          100% { opacity: 0; }
        }

        /* ─── WIPE MAGICO (44-53%) ─────────────────────────────────────── */
        .rre-wipe {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%,-50%);
          width: 0; height: 0; border-radius: 999px;
          background: #FAF8F4;
          z-index: 6;
          opacity: 0;
          animation: rreWipe 18s cubic-bezier(0.65, 0, 0.35, 1) forwards;
        }
        @keyframes rreWipe {
          0%, 44% { width: 0; height: 0; opacity: 0; }
          46% { width: 80px; height: 80px; opacity: 1; }
          53% { width: 3000px; height: 3000px; opacity: 1; }
          55% { width: 3000px; height: 3000px; opacity: 0; }
          100% { width: 3000px; height: 3000px; opacity: 0; visibility: hidden; }
        }

        /* ─── DOPO (53-78%) ─────────────────────────────────────────────── */
        .rre-after {
          position: absolute; inset: 0;
          background: #FAF8F4;
          z-index: 3; opacity: 0;
          animation: rreAfterOpa 18s linear forwards;
        }
        @keyframes rreAfterOpa {
          0%, 48% { opacity: 0; }
          53% { opacity: 1; }
          85% { opacity: 1; }
          88% { opacity: 0; }
          100% { opacity: 0; visibility: hidden; }
        }
        .rre-after-img {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          object-fit: cover; object-position: center 35%;
          transform: scale(1.0);
          animation: rreAfterKenBurns 18s ease-out forwards;
        }
        @keyframes rreAfterKenBurns {
          0%, 52% { transform: scale(1.05); }
          78% { transform: scale(1.12); }
          100% { transform: scale(1.12); }
        }
        .rre-after-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.0) 40%, rgba(28,28,26,0.55) 100%);
        }
        .rre-after-badge {
          position: absolute; top: 120px; right: 60px;
          display: inline-flex; align-items: center; gap: 18px;
          padding: 14px 14px 14px 26px;
          background: #1C1C1A;
          border-radius: 999px;
          opacity: 0; transform: translateX(30px);
          animation: rreAfterBadge 18s ease-out forwards;
        }
        @keyframes rreAfterBadge {
          0%, 54% { opacity: 0; transform: translateX(30px); }
          59% { opacity: 1; transform: translateX(0); }
          85% { opacity: 1; transform: translateX(0); }
          88% { opacity: 0; transform: translateX(-12px); }
          100% { opacity: 0; }
        }
        .rre-after-badge-text {
          font-family: 'Montserrat', sans-serif;
          font-weight: 800; font-size: 28px;
          letter-spacing: 0.18em;
          color: #FAF8F4;
          text-transform: uppercase;
        }
        .rre-after-badge-dot {
          width: 36px; height: 36px;
          border-radius: 999px;
          background: #A8B89A;
          flex-shrink: 0;
        }
        .rre-after-line1 {
          position: absolute; bottom: 280px; left: 60px; right: 60px;
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-style: italic; font-weight: 500;
          font-size: 70px; line-height: 1.0;
          color: #FAF8F4; letter-spacing: -0.012em;
          opacity: 0; transform: translateY(20px);
          text-shadow: 0 4px 24px rgba(0,0,0,0.4);
          animation: rreAfterLine1 18s ease-out forwards;
        }
        @keyframes rreAfterLine1 {
          0%, 58% { opacity: 0; transform: translateY(20px); }
          63% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          88% { opacity: 0; }
          100% { opacity: 0; }
        }
        .rre-after-line2 {
          position: absolute; bottom: 180px; left: 60px; right: 60px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700; font-size: 90px; line-height: 1;
          color: #A8B89A; letter-spacing: -0.018em;
          opacity: 0; transform: translateY(20px);
          text-shadow: 0 4px 24px rgba(0,0,0,0.4);
          animation: rreAfterLine2 18s ease-out forwards;
        }
        @keyframes rreAfterLine2 {
          0%, 62% { opacity: 0; transform: translateY(20px); }
          67% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          88% { opacity: 0; }
          100% { opacity: 0; }
        }

        /* ─── STAT BLOCK (78-88%) ──────────────────────────────────────── */
        .rre-stat {
          position: absolute; inset: 0;
          background: #1C1C1A;
          z-index: 4;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 48px;
          opacity: 0;
          animation: rreStatOpa 18s linear forwards;
        }
        @keyframes rreStatOpa {
          0%, 78% { opacity: 0; visibility: hidden; }
          82% { opacity: 1; visibility: visible; }
          88% { opacity: 1; visibility: visible; }
          92% { opacity: 0; visibility: hidden; }
          100% { opacity: 0; visibility: hidden; }
        }
        .rre-stat-item {
          text-align: center;
          opacity: 0; transform: translateY(30px);
        }
        .rre-stat-1 { animation: rreStatItem1 18s ease-out forwards; }
        .rre-stat-2 { animation: rreStatItem2 18s ease-out forwards; }
        .rre-stat-3 { animation: rreStatItem3 18s ease-out forwards; }
        @keyframes rreStatItem1 {
          0%, 80% { opacity: 0; transform: translateY(30px); }
          84% { opacity: 1; transform: translateY(0); }
          88% { opacity: 1; transform: translateY(0); }
          92% { opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes rreStatItem2 {
          0%, 82% { opacity: 0; transform: translateY(30px); }
          86% { opacity: 1; transform: translateY(0); }
          88% { opacity: 1; transform: translateY(0); }
          92% { opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes rreStatItem3 {
          0%, 84% { opacity: 0; transform: translateY(30px); }
          88% { opacity: 1; transform: translateY(0); }
          92% { opacity: 0; }
          100% { opacity: 0; }
        }
        .rre-stat-num {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-style: italic; font-weight: 600;
          font-size: 180px; line-height: 0.9;
          color: #A8B89A; letter-spacing: -0.02em;
          margin-bottom: 12px;
        }
        .rre-stat-label {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700; font-size: 22px;
          letter-spacing: 0.36em; color: rgba(250,248,244,0.7);
          text-transform: uppercase;
        }

        /* ─── OUTRO (88-100%) ──────────────────────────────────────────── */
        .rre-outro {
          position: absolute; inset: 0;
          background: #1C1C1A;
          z-index: 5;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          opacity: 0;
          animation: rreOutroOpa 18s linear forwards;
        }
        @keyframes rreOutroOpa {
          0%, 90% { opacity: 0; }
          94% { opacity: 1; }
          100% { opacity: 1; }
        }
        .rre-outro > * {
          opacity: 0;
        }
        .rre-outro-tag {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-style: italic; font-weight: 500;
          font-size: 84px; line-height: 1.06;
          color: #FAF8F4; text-align: center;
          letter-spacing: -0.012em;
          margin-bottom: 48px;
          animation: rreOutroTag 18s ease-out forwards;
        }
        .rre-outro-tag strong {
          font-style: normal; font-family: 'Montserrat', sans-serif;
          font-weight: 700; color: #A8B89A;
          font-size: 100px;
        }
        @keyframes rreOutroTag {
          0%, 91% { opacity: 0; transform: translateY(20px); }
          95% { opacity: 1; transform: translateY(0); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .rre-outro-mark {
          width: 180px; height: 180px; margin-bottom: 28px;
          filter: brightness(0) invert(0.93);
          animation: rreOutroMark 18s ease-out forwards;
        }
        @keyframes rreOutroMark {
          0%, 93% { opacity: 0; transform: scale(0.94); }
          96% { opacity: 1; transform: scale(1); }
          100% { opacity: 1; transform: scale(1); }
        }
        .rre-outro-url {
          font-family: 'Montserrat', sans-serif;
          font-weight: 500; font-size: 42px;
          color: #A8B89A; letter-spacing: 0.01em;
          margin-bottom: 36px;
          animation: rreOutroUrl 18s ease-out forwards;
        }
        @keyframes rreOutroUrl {
          0%, 95% { opacity: 0; }
          98% { opacity: 1; }
          100% { opacity: 1; }
        }
        .rre-outro-cta {
          font-family: 'Montserrat', sans-serif;
          font-weight: 600; font-size: 26px;
          color: #B57C5E; letter-spacing: 0.22em;
          text-transform: uppercase;
          animation: rreOutroCta 18s ease-out forwards;
        }
        @keyframes rreOutroCta {
          0%, 96% { opacity: 0; }
          99% { opacity: 1; }
          100% { opacity: 1; }
        }
      `}</style>

      <div
        data-testid="reel-emotional"
        style={{
          width: 1080, height: 1920,
          background: "#1C1C1A",
          fontFamily: "'Montserrat', system-ui, sans-serif",
          position: "relative", overflow: "hidden", margin: 0,
        }}
      >
        {/* ───── COVER ───── */}
        <div className="rre-cover">
          <img className="rre-cover-mark" src="/logo/mark-symbol-128.png" alt=""/>
          <div className="rre-cover-line1">Una foto</div>
          <div className="rre-cover-line2">basta.</div>
          <div className="rre-cover-tag">guarda</div>
        </div>

        {/* ───── PRIMA ───── */}
        <div className="rre-before">
          <img className="rre-before-img" src="/landing/emotional/camera-before.jpg" alt=""/>
          <div className="rre-before-overlay"/>
          <div className="rre-before-badge">
            <div className="rre-before-badge-dot"/>
            <div className="rre-before-badge-text">Prima</div>
          </div>
          <div className="rre-before-caption">
            Una camera<br/>vera. Spettinata.
          </div>
        </div>

        {/* ───── WIPE ───── */}
        <div className="rre-wipe"/>

        {/* ───── DOPO ───── */}
        <div className="rre-after">
          <img className="rre-after-img" src="/landing/emotional/camera-after.jpg" alt=""/>
          <div className="rre-after-overlay"/>
          <div className="rre-after-badge">
            <div className="rre-after-badge-text">Dopo</div>
            <div className="rre-after-badge-dot"/>
          </div>
          <div className="rre-after-line1">In cinque secondi,</div>
          <div className="rre-after-line2">pronta da vendere.</div>
        </div>

        {/* ───── STAT BLOCK ───── */}
        <div className="rre-stat">
          <div className="rre-stat-item rre-stat-1">
            <div className="rre-stat-num">5</div>
            <div className="rre-stat-label">secondi</div>
          </div>
          <div className="rre-stat-item rre-stat-2">
            <div className="rre-stat-num">0</div>
            <div className="rre-stat-label">staging fisico</div>
          </div>
          <div className="rre-stat-item rre-stat-3">
            <div className="rre-stat-num">1</div>
            <div className="rre-stat-label">click</div>
          </div>
        </div>

        {/* ───── OUTRO ───── */}
        <div className="rre-outro">
          <div className="rre-outro-tag">
            Le case<br/>
            <strong>le vendi tu.</strong>
          </div>
          <img className="rre-outro-mark" src="/logo/mark-symbol-128.png" alt=""/>
          <div className="rre-outro-url">prontoimmo.it</div>
          <div className="rre-outro-cta">— provala gratuitamente —</div>
        </div>
      </div>
    </>
  );
}
